import { useState, useCallback, useRef } from "react";
import { useController, useFormContext } from "react-hook-form";
import { CircleAlert } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { Label } from "@themes/pollinator/ui/Label";
import { FieldError } from "@themes/pollinator/ui/FieldError";
import { Type, TypeVariant } from "@themes/pollinator/ui/Type";

// Import UI components
import { Input } from "@themes/pollinator/ui/Input";
import { Textarea } from "@themes/pollinator/ui/Textarea";
import { Select } from "@themes/pollinator/ui/Select";
import { RadioGroup } from "@themes/pollinator/ui/RadioGroup";
import { YesNoButtons } from "@themes/pollinator/ui/YesNoButtons";
import { SelectChips } from "@themes/pollinator/ui/SelectChips";

// Import quiz-specific components
// Direct import to avoid circular dependency (QuestionSet → Quiz → QuestionSet)
import { ForceYesInput } from "../ForceYesInput";
import { FileAnswerInputContainer } from "../FileAnswerInput";
import { ImageFieldContainer, type AltTextState } from "../ImageField";
import { AffiliationListContainer } from "@platform/features/affiliations";

import type { QuizFieldType, QuizFormComponent } from "@core/features/quiz";

/**
 * Counts words in a string by splitting on whitespace.
 */
function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

// Component registry for quiz fields
const componentRegistry: Record<QuizFormComponent, React.ComponentType<any>> = {
  Input,
  Textarea,
  Select,
  RadioGroup,
  YesNoButtons,
  SelectChips,
  ForceYesInput,
  AffiliationListContainer,
  FileAnswerInput: FileAnswerInputContainer,
  ImageField: ImageFieldContainer,
};

// Omit labelVariant from QuizFieldType and use TypeVariant instead
export type Props = Omit<QuizFieldType, "labelVariant"> & {
  className?: string;
  /** Label variant for typography */
  labelVariant?: TypeVariant;
  /** Help text shown below label */
  helpText?: string;
  /** Layout direction - vertical (default/wizard) or horizontal (form-style like ProfileForm) */
  layout?: "vertical" | "horizontal";
  /** Display variant for YesNoButtons - "buttons" (default) or "chips" */
  yesNoVariant?: "buttons" | "chips";
  /** Direction for RadioGroup - "horizontal" (default) or "vertical" */
  radioGroupDirection?: "horizontal" | "vertical";
  /** Show character count below textarea (longtext only) */
  showCharacterCount?: boolean;
  /** Show word count below textarea (longtext only) */
  showWordCount?: boolean;
  /** Maximum word count for longtext validation */
  maxWords?: number;
  /** Maximum character count for longtext validation */
  maxCharacters?: number;
  /** Minimum character count for longtext validation */
  minCharacters?: number;
};

/**
 * Quiz form field component that renders dynamic fields based on quiz questions.
 * Handles RHF binding, label, and error display for quiz-specific field types.
 */
export function QuizFormField({
  name,
  component,
  label,
  helpText,
  required = false,
  image,
  labelVariant = "body1",
  className,
  options,
  maxItems,
  minItems,
  placeholder,
  isCustomComponent = false,
  questionType,
  maxFiles,
  validation_rules,
  initialFiles,
  layout = "vertical",
  yesNoVariant,
  radioGroupDirection,
  inputType,
  showCharacterCount,
  showWordCount,
  maxWords,
  maxCharacters,
}: Props) {
  const isHorizontal = layout === "horizontal";
  const { control, formState } = useFormContext();
  const { isSubmitting } = formState;

  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  // State for max items exceeded error (RadioGroup/SelectChips)
  const [maxItemsError, setMaxItemsError] = useState<string | null>(null);
  const maxItemsErrorTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleMaxItemsExceeded = useCallback((max: number) => {
    setMaxItemsError(
      `You have already selected ${max} items. Uncheck a previous item to select something else.`
    );
    // Clear any existing timer
    if (maxItemsErrorTimerRef.current) {
      clearTimeout(maxItemsErrorTimerRef.current);
    }
    // Auto-clear after 5 seconds
    maxItemsErrorTimerRef.current = setTimeout(() => {
      setMaxItemsError(null);
    }, 5000);
  }, []);

  // State for ImageField alt text (managed by ImageFieldContainer)
  const [altTextState, setAltTextState] = useState<AltTextState | null>(null);
  const [altTextTouched, setAltTextTouched] = useState(false);

  const handleAltTextStateChange = useCallback((state: AltTextState) => {
    setAltTextState(state);
  }, []);

  const Component = componentRegistry[component];

  if (!Component) {
    console.warn(`Unknown quiz component type: ${component}`);
    return null;
  }

  // Build props for the component
  const fieldProps: Record<string, unknown> = {
    name,
    id: name,
    disabled: isSubmitting,
    error: !!error,
    "aria-describedby": error ? `${name}-error` : undefined,
    "aria-labelledby": label ? `${name}-label` : undefined,
    "aria-required": required || undefined,
  };

  // Handle value/onChange based on component type
  switch (component) {
    case "Select":
      fieldProps.options = options;
      fieldProps.placeholder = placeholder;
      fieldProps.value = options?.filter((o) =>
        (value as number[])?.includes(o.value)
      );
      fieldProps.onChange = (
        selected: { label: string; value: number } | { label: string; value: number }[] | null
      ) => {
        if (Array.isArray(selected)) {
          onChange(selected.map((o) => o.value));
        } else if (selected) {
          onChange([selected.value]);
        } else {
          onChange([]);
        }
      };
      fieldProps.onBlur = onBlur;
      break;

    case "RadioGroup":
      fieldProps.options = options;
      fieldProps.value = value ?? [];
      fieldProps.onChange = onChange;
      fieldProps.onBlur = onBlur;
      if (maxItems !== undefined) {
        fieldProps.maxItems = maxItems;
        if (maxItems > 1) {
          fieldProps.onMaxItemsExceeded = handleMaxItemsExceeded;
        }
      }
      if (radioGroupDirection) {
        fieldProps.direction = radioGroupDirection;
      }
      break;

    case "YesNoButtons":
      fieldProps.options = options;
      fieldProps.value = value ?? [];
      fieldProps.onChange = onChange;
      fieldProps.onBlur = onBlur;
      if (yesNoVariant) {
        fieldProps.variant = yesNoVariant;
      }
      break;

    case "SelectChips":
      fieldProps.options = options;
      fieldProps.value = value ?? [];
      fieldProps.onChange = onChange;
      fieldProps.onBlur = onBlur;
      if (maxItems !== undefined) {
        fieldProps.maxItems = maxItems;
        if (maxItems > 1) {
          fieldProps.onMaxItemsExceeded = handleMaxItemsExceeded;
        }
      }
      break;

    case "ForceYesInput":
      fieldProps.options = options;
      fieldProps.value = value ?? true;
      fieldProps.onChange = onChange;
      fieldProps.onBlur = onBlur;
      break;

    case "AffiliationListContainer":
      fieldProps.value = value ?? [];
      fieldProps.onChange = onChange;
      fieldProps.onBlur = onBlur;
      if (maxItems !== undefined) {
        fieldProps.maxItems = maxItems;
      }
      if (minItems !== undefined) {
        fieldProps.minItems = minItems;
      }
      break;

    case "FileAnswerInput":
      fieldProps.value = value ?? [];
      fieldProps.onChange = onChange;
      fieldProps.onBlur = onBlur;
      fieldProps.questionType = questionType;
      fieldProps.maxFiles = maxFiles;
      fieldProps.validationRules = validation_rules;
      fieldProps.initialFiles = initialFiles;
      break;

    case "ImageField":
      fieldProps.value = value ?? [];
      fieldProps.onChange = onChange;
      fieldProps.onBlur = onBlur;
      fieldProps.initialFiles = initialFiles;
      fieldProps.onAltTextStateChange = handleAltTextStateChange;
      break;

    case "Input":
      fieldProps.value = value ?? "";
      fieldProps.onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
      };
      fieldProps.onBlur = onBlur;
      fieldProps.placeholder = placeholder;
      if (inputType) fieldProps.type = inputType;
      break;

    case "Textarea":
      fieldProps.value = value ?? "";
      fieldProps.onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
      };
      fieldProps.onBlur = onBlur;
      fieldProps.placeholder = placeholder;
      break;

    default:
      fieldProps.value = value;
      fieldProps.onChange = onChange;
      fieldProps.onBlur = onBlur;
      if (options) fieldProps.options = options;
      if (placeholder) fieldProps.placeholder = placeholder;
      break;
  }

  // Check if this is an ImageField that needs alt text rendering
  const isImageFieldWithAltText = component === "ImageField" && altTextState?.hasImage;
  const altTextLabel = `${label || "Image"} Alt Text`;
  const showAltTextError = altTextTouched && isImageFieldWithAltText &&
    (!altTextState?.value || altTextState.value.trim() === "");

  return (
    <>
      <div
        className={twMerge(
          "relative flex flex-col gap-1 transition-opacity duration-100 p-1",
          isHorizontal && "md:flex-row md:items-start",
          className,
          isSubmitting && "opacity-50"
        )}
      >
        {image && (
          <img
            src={image}
            alt={label}
            className="w-32 object-contain mx-auto mb-8"
          />
        )}

        {label && (
          <div
            className={twMerge(
              "flex flex-col gap-1 md:gap-2",
              isHorizontal && "mb-0.5 w-full md:mb-0 md:mr-8 md:w-1/3"
            )}
          >
            <Label
              htmlFor={name}
              id={`${name}-label`}
              error={!!error}
              required={required}
              typeVariant={labelVariant}
            >
              {label}
            </Label>
            {helpText && (
              <Type variant="caption" className="text-brand-700">
                {helpText}
              </Type>
            )}
          </div>
        )}

        {/* Wrapper to match FormFieldBase structure */}
        <div className="flex flex-col gap-2 w-full">
          <div
            className={twMerge(
              "relative flex w-full items-center",
              isHorizontal && "flex w-full flex-col gap-4 md:w-2/3 md:flex-row"
            )}
          >
            <div className="flex flex-col relative w-full gap-1.5">
              <Component {...fieldProps} />

              {/* Character/word count display for longtext fields */}
              {component === "Textarea" && (showCharacterCount || showWordCount) && (() => {
                const text = (value as string) ?? "";
                const charCount = text.length;
                const wordCount = countWords(text);
                const isOverCharLimit = maxCharacters != null && charCount > maxCharacters;
                const isOverWordLimit = maxWords != null && wordCount > maxWords;

                return (
                  <div className="flex justify-end">
                    {showCharacterCount && (
                      <Type
                        variant="caption"
                        className={isOverCharLimit ? "text-error-600" : "text-brand-600"}
                      >
                        {charCount}
                        {maxCharacters != null && ` / ${maxCharacters}`} characters
                      </Type>
                    )}
                    {showWordCount && (
                      <Type
                        variant="caption"
                        className={isOverWordLimit ? "text-error-600" : "text-brand-600"}
                      >
                        {wordCount}
                        {maxWords != null && ` / ${maxWords}`} words
                      </Type>
                    )}
                  </div>
                );
              })()}

              {error && <FieldError id={`${name}-error`} message={error.message} />}
              {maxItemsError && !error && (
                <FieldError id={`${name}-max-items-error`} message={maxItemsError} />
              )}

              {error && !isCustomComponent && (
                <CircleAlert
                  className="text-error-600 absolute right-3 top-3 h-4 w-4"
                  aria-hidden="true"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alt text field for ImageField - rendered in form flow */}
      {isImageFieldWithAltText && (
        <div
          className={twMerge(
            "relative flex flex-col gap-1 transition-opacity duration-100 p-1",
            isHorizontal && "md:flex-row md:items-start",
            className,
            isSubmitting && "opacity-50"
          )}
        >
          {/* Label section */}
          <div
            className={twMerge(
              "flex flex-col gap-1 md:gap-2",
              isHorizontal && "mb-0.5 w-full md:mb-0 md:mr-8 md:w-1/3"
            )}
          >
            <Label
              htmlFor={`${name}-altText`}
              id={`${name}-altText-label`}
              error={showAltTextError}
              required
              typeVariant={labelVariant}
            >
              {altTextLabel}
            </Label>
          </div>

          {/* Content wrapper */}
          <div className="flex flex-col gap-2 w-full">
            <div
              className={twMerge(
                "relative flex w-full items-center",
                isHorizontal && "flex w-full flex-col gap-4 md:w-2/3 md:flex-row"
              )}
            >
              <div className="flex flex-col relative w-full gap-1.5">
                <Input
                  id={`${name}-altText`}
                  name={`${name}-altText`}
                  value={altTextState?.value ?? ""}
                  onChange={(e) => altTextState?.onChange(e.target.value)}
                  onBlur={() => {
                    setAltTextTouched(true);
                    altTextState?.onBlur();
                  }}
                  placeholder="Describe this image for accessibility"
                  error={showAltTextError}
                  disabled={isSubmitting}
                  aria-labelledby={`${name}-altText-label`}
                  aria-describedby={showAltTextError ? `${name}-altText-error` : undefined}
                  aria-invalid={showAltTextError}
                  aria-required
                />
              </div>
            </div>
            {showAltTextError && (
              <FieldError id={`${name}-altText-error`} message="Alt text is required" />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default QuizFormField;
